import { useCallback, useState } from 'react';

import { TextButton } from '@opendoor/bricks-next';
import { Box } from '@opendoor/bricks/core';
import { useRouter } from 'next/router';

import {
  OdProtosSellReceptionData_SellerInput_AnalyticsMetadata_Product,
  OdProtosSellReceptionData_SellerInput_Channel,
} from '__generated__/athena';

import { useT } from 'helpers/i18n';

import AddressInput, {
  AddressValidationResult,
  AutoCompletePrediction,
  SelectedAddress,
} from './AddressInput';

export type { AutoCompletePrediction };

export type NovoAddressInputProps = {
  id: string;
  placeholder?: string;
  disabled?: boolean;
  hideLabel?: boolean;
  onAddressSelect: (address: SelectedAddress) => void;
  onAddressValidated?: (result: AddressValidationResult) => void;
  validateAddress?: boolean;
  onPredictionsChange?: (predictions: AutoCompletePrediction[]) => void;
  onError?: (error: Error) => void;
  product?: OdProtosSellReceptionData_SellerInput_AnalyticsMetadata_Product;
  pageSetPath?: string;
  trackingKeywords?: string;
  partnerReferralId?: string;
  channel?: OdProtosSellReceptionData_SellerInput_Channel;
};

const ManualEntryButton: React.FC<{
  onMouseDown: (e: React.MouseEvent) => void;
  onPress: () => void;
}> = ({ onMouseDown, onPress }) => {
  const { t } = useT();

  return (
    <TextButton
      zIndex={2}
      onMouseDown={onMouseDown}
      label={t('homepage.titles.address', 'Enter address manually instead')}
      analyticsName="cosmos-address-input-manual-entry"
      type="button"
      onPress={onPress}
      width={'100%'}
      variant="primary"
      size="medium"
      px={20}
      paddingTop={10}
      paddingBottom={20}
    />
  );
};

const NovoAddressInput: React.FC<NovoAddressInputProps> = ({
  id,
  placeholder,
  disabled,
  hideLabel,
  onAddressSelect,
  onAddressValidated,
  validateAddress,
  onPredictionsChange,
  onError,
  pageSetPath,
  trackingKeywords,
  partnerReferralId,
  channel,
}) => {
  const [predictions, setPredictions] = useState<AutoCompletePrediction[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const router = useRouter();

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
  }, []);

  const handleManualEntryPress = useCallback(() => {
    router.push({
      pathname: '/manual-address-entry',
      query: {
        ...(trackingKeywords != null && { trackingKeywords }),
        ...(partnerReferralId != null && { partnerReferralId }),
        ...(pageSetPath != null && { pageSetPath }),
        ...(channel != null && { channel }),
      },
    });
  }, [router, trackingKeywords, partnerReferralId, pageSetPath, channel]);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
  }, []);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
  }, []);

  const handlePredictionsChange = (newPredictions: AutoCompletePrediction[]) => {
    setPredictions(newPredictions);
    onPredictionsChange?.(newPredictions);
  };

  const handleSearchTermChange = useCallback((newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
  }, []);

  const customAction =
    predictions.length < 5 && searchTerm !== '' && isFocused ? (
      <ManualEntryButton onMouseDown={handleMouseDown} onPress={handleManualEntryPress} />
    ) : undefined;

  return (
    <Box className="od-address-input">
      <Box position={'relative'} zIndex={2} className="cta-input-form">
        <AddressInput
          id={id}
          label="Enter your home address"
          hideLabel={hideLabel}
          placeholder={placeholder}
          disabled={disabled}
          onAddressSelect={onAddressSelect}
          onAddressValidated={onAddressValidated}
          validateAddress={validateAddress}
          onPredictionsChange={handlePredictionsChange}
          onSearchTermChange={handleSearchTermChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onError={onError}
          customAction={customAction}
          listBoxId="cb1-listbox"
          listBoxAriaLabel="addresses"
        />
      </Box>
    </Box>
  );
};

export default NovoAddressInput;
